import { useContext } from 'react';

import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import type { ParsedUrlQuery } from '@confluence/route-manager-utils/entry-points/types';

import { collabSpotFromParams } from './collab-spot';

export type GetDatabaseParamsResult = {
	'databases-frontend': string | undefined;
	'canvas-server': number | undefined;
	'canvas-relay': number | undefined;
	headless: boolean;
	isSmartLinkEmbed: boolean;
	presentationMode?: string;
	bridgeClient?: string;
	isRovoParentProduct: boolean;
};

const envList = ['dev', 'branch', 'staging', 'hello'];

export const PARENT_PRODUCT_QUERY_PARAM = 'parentProduct';
export const PARENT_PRODUCT_SMART_LINK = 'smartLink';
export const PARENT_PRODUCT_ROVO = 'rovo';
export const EMBEDDED_THEME_STATE = 'themeState';
export const PRESENTATION_MODE_MOBILE_APP = 'mobileApp';
export const PRESENTATION_MODE_QUERY_PARAM = 'presentationMode';
export const BRIDGE_CLIENT_QUERY_PARAM = 'bridgeClient';

const HEADLESS_QUERY_PARAM = 'headless';

const retrieveQueryParameter = (queryParams: ParsedUrlQuery, key: string): string | undefined => {
	return queryParams?.[key] && !Array.isArray(queryParams?.[key])
		? (queryParams?.[key] as string) // TypeScript doesn't acknowledge the !Array.isArray
		: undefined;
};

export const useDatabaseParams = (): GetDatabaseParamsResult => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	// Versioned deploys are currently only supported in development and internal
	// environments
	const allowDevParams = envList.some((e) => e === process.env.CLOUD_ENV);
	const parentProduct = retrieveQueryParameter(queryParams, PARENT_PRODUCT_QUERY_PARAM);
	const presentationMode = retrieveQueryParameter(queryParams, PRESENTATION_MODE_QUERY_PARAM);
	const bridgeClient = retrieveQueryParameter(queryParams, BRIDGE_CLIENT_QUERY_PARAM);
	const headless = retrieveQueryParameter(queryParams, HEADLESS_QUERY_PARAM);

	const frontendVersion =
		allowDevParams &&
		queryParams?.['databases-frontend'] &&
		!Array.isArray(queryParams?.['databases-frontend'])
			? queryParams?.['databases-frontend']
			: undefined;

	const canvasServerSpot = allowDevParams
		? collabSpotFromParams(queryParams, 'canvas-server')
		: undefined;

	const canvasRelaySpot = allowDevParams
		? collabSpotFromParams(queryParams, 'canvas-relay')
		: undefined;

	return {
		'databases-frontend': frontendVersion,
		'canvas-server': canvasServerSpot,
		'canvas-relay': canvasRelaySpot,
		headless: headless === 'true',
		isSmartLinkEmbed: parentProduct === PARENT_PRODUCT_SMART_LINK,
		presentationMode,
		bridgeClient,
		isRovoParentProduct: parentProduct === PARENT_PRODUCT_ROVO,
	};
};
